import { useState } from "react";
import { articleThumb } from "../lib/article-thumb";
import { categoryLabelAr } from "../lib/categories";
import { ARTICLE_LAYOUTS, type BlockLayout, type SectionArticle } from "../lib/segment-types";
import { LayoutPicker } from "./LayoutPicker";
import { Bidi } from "./primitives";
import { IconArrowDown, IconArrowUp, IconClose, IconPencil, IconSparkle } from "./icons";

/**
 * One article inside a section, shown the way it will appear to a reader —
 * thumbnail, headline, category and reading time, and the link. An editor
 * arranging an issue is making an editorial judgement about what sits next to
 * what, which a row of titles and URLs does not support.
 */
export function ArticleRow({
  article, index, count, locked, defaultOpen, onMove, onRemove, onChange,
}: {
  article: SectionArticle;
  index: number;
  count: number;
  locked?: boolean;
  /** A link just added opens ready for its description to be written. */
  defaultOpen?: boolean;
  onMove?: (dir: -1 | 1) => void;
  onRemove?: () => void;
  /** Edit this link's description, picture and where the picture sits. */
  onChange?: (patch: Partial<SectionArticle>) => void;
}) {
  const [open, setOpen] = useState(Boolean(defaultOpen));
  const meta = [
    article.category,
    article.read_minutes ? `${article.read_minutes} min read` : null,
  ].filter(Boolean).join(" · ");

  return (
    <div className="nl-arow">
      <img
        className="nl-arow-thumb"
        src={article.image || articleThumb(article.category, index)}
        alt=""
        aria-hidden
        width={44}
        height={44}
      />
      <div className="nl-arow-copy">
        <span className="nl-arow-title"><Bidi>{article.title}</Bidi></span>
        {meta && <span className="nl-arow-meta">{meta}</span>}
      </div>
      <a
        className="nl-arow-cta"
        href={article.url || "#"}
        target="_blank"
        rel="noopener noreferrer"
        dir="rtl"
        title={article.url}
      >
        اقرأ المزيد
      </a>
      {!locked && (
        <span className="nl-arow-tools">
          {onChange && (
            <button type="button" onClick={() => setOpen((v) => !v)}
              aria-expanded={open} aria-label="Edit this link"><IconPencil /></button>
          )}
          <button type="button" onClick={() => onMove?.(-1)} disabled={index === 0} aria-label="Move article up"><IconArrowUp /></button>
          <button type="button" onClick={() => onMove?.(1)} disabled={index === count - 1} aria-label="Move article down"><IconArrowDown /></button>
          <button type="button" onClick={onRemove} aria-label="Remove article"><IconClose /></button>
        </span>
      )}

      {/* Every link gets its own words and its own picture placement — the
          description is what the editor wants said about it, which is rarely
          the summary the CMS carries. */}
      {open && onChange && !locked && (
        <div className="nl-arow-edit">
          <label className="nl-field nl-field-explained">
            <span>Description</span>
            <textarea
              className="nl-textarea" dir="auto" rows={2}
              placeholder="What you want said about this link"
              value={article.excerpt ?? ""}
              onChange={(e) => onChange({ excerpt: e.target.value })}
            />
            <em className="nl-field-hint">Shown under the link.</em>
          </label>

          <label className="nl-field nl-field-explained">
            <span>Picture</span>
            <input
              className="nl-input" type="url" inputMode="url"
              placeholder="https://cdn.altibbi.com/…"
              value={article.image ?? ""}
              onChange={(e) => onChange({ image: e.target.value })}
            />
            <em className="nl-field-hint">Filled from the article when you pick one. Replace it here.</em>
          </label>

          <div className="nl-field nl-field-explained">
            <span>Where the picture sits</span>
            <LayoutPicker
              value={(article.layout ?? "image-start") as BlockLayout}
              options={ARTICLE_LAYOUTS.map((l) => l.value)}
              onChange={(layout) => onChange({ layout: layout as SectionArticle["layout"] })}
            />
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * The slots that have no pinned article, summarised in one line.
 *
 * There used to be one row per empty slot, each carrying the same sentence —
 * a three-article section said the same thing three times, a six-article one
 * six times. The count is the only thing that varies, so it is the only thing
 * worth repeating.
 */
export function AutoSummary({
  count, category,
  /** True when some articles above are pinned, so this continues the list. */
  hasPinned,
}: {
  count: number;
  category: string | null;
  hasPinned?: boolean;
}) {
  if (count <= 0) return null;
  const source = category
    ? <strong><Bidi>{categoryLabelAr(category)}</Bidi></strong>
    : <strong>each reader&rsquo;s own category</strong>;

  return (
    <div className="nl-autosum">
      <IconSparkle className="nl-autosum-mark" />
      <span>
        {hasPinned ? <>{count} more, chosen from {source}</>
          : count === 1 ? <>One article, chosen from {source}</>
          : <>{count} articles, chosen from {source}</>}
        {" "}when this sends.
      </span>
    </div>
  );
}
